/**
 * UserReport.tsx — Customer (org-scope) Report View
 * Fetches a specific published report by reportId from URL params.
 * Read-only view: no edit/submit/retry. Download available when pdf_available.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowDownRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  Download,
  FileText,
  Loader2,
  Minus,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { reportService } from "../api/services/reportService";
import { Toaster } from "../components/ui/Toaster";
import { useSettings } from "../context/SettingsContext";
import { useAuthStore } from "../stores/authStore";
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatPercentage,
} from "../utils/formatters";
import MetricsByCampaignAdGroupAdTable from "./Reports/components/MetricsByCampaignAdGroupAdTable";
import MetricsByCreativeTable from "./Reports/components/MetricsByCreativeTable";
import TrafficSourceTable from "./Reports/components/TrafficSourceTable";
import type {
  CampaignAdData,
  CampaignAdGroupByAd,
  CampaignAdRow,
  CampaignAdTotals,
  CampaignWithAds,
  CreativeData,
  CreativeRow,
  ReportJobData,
  TrafficSourceData,
  TrafficSourceRow,
} from "./Reports/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Split a single text string into { title, desc } for display.
 * Tries to split at first colon, then first period, then truncates.
 */
function splitText(text: string): { title: string; desc: string } {
  const colonIdx = text.indexOf(":");
  if (colonIdx > 0 && colonIdx < 80) {
    return {
      title: text.slice(0, colonIdx).trim(),
      desc: text.slice(colonIdx + 1).trim(),
    };
  }
  const periodIdx = text.indexOf(".");
  if (periodIdx > 0 && periodIdx < 100) {
    return {
      title: text.slice(0, periodIdx).trim(),
      desc: text.slice(periodIdx + 1).trim(),
    };
  }
  const cut = Math.min(text.length, 80);
  return { title: text.slice(0, cut), desc: text.slice(cut).trim() };
}

/** Returns Tailwind colour classes for a change badge (positive/negative/neutral). */
function changeBadgeClass(
  value: number | null | undefined,
  darkMode = false,
): string {
  if (value === null || value === undefined || value === 0)
    return darkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-600";
  return value > 0
    ? darkMode
      ? "bg-green-900/60 text-green-400"
      : "bg-green-50 text-green-700"
    : darkMode
      ? "bg-red-900/60 text-red-400"
      : "bg-red-50 text-red-600";
}

/** Formats a decimal change value as a signed percentage string, or "—". */
function formatChange(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return formatPercentage(value, true); // isChange=true adds +/- prefix
}

// ---------------------------------------------------------------------------
// Polling helper — waits until a report job reaches SUCCESS or FAILED.
// Resolves with the final ReportJobData or throws on failure / timeout.
// ---------------------------------------------------------------------------
async function pollReportJob(
  jobId: number,
  signal: AbortSignal,
  maxAttempts = 30,
  delayMs = 2000,
): Promise<ReportJobData> {
  for (let i = 0; i < maxAttempts; i++) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const resp = await reportService.getReportJobStatus(jobId);
    if (!resp.success) throw new Error("Failed to check report job status");
    const { status, error_message } = resp.responseData;
    if (status === "SUCCESS") return resp.responseData;
    if (status === "FAILED")
      throw new Error(error_message ?? "Report generation failed");
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, delayMs);
      signal.addEventListener("abort", () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      });
    });
  }
  throw new Error("Report generation timed out");
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WebAnalyticsReport() {
  const { t } = useTranslation();
  const { darkMode, language } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId");

  // Redirect to My Reports if no reportId is present in the URL or is an invalid value
  const isValidReportId = !!reportId && reportId !== "undefined";
  useEffect(() => {
    if (!isValidReportId) {
      navigate("/app/my-reports", { replace: true });
    }
  }, [isValidReportId, navigate]);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const {
    data: reportData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["report", reportId],
    queryFn: () => reportService.getReportById(reportId!),
    enabled: isValidReportId,
    retry: 1,
  });

  // -------------------------------------------------------------------------
  // PDF download state (generate → poll → download flow)
  // -------------------------------------------------------------------------

  type DownloadState = "idle" | "generating" | "polling" | "error";
  const [downloadState, setDownloadState] = useState<DownloadState>("idle");
  const abortRef = useRef<AbortController | null>(null);

  // -------------------------------------------------------------------------
  // Refresh Data (BQ Sync) — only visible to platform / tenant scope users
  // -------------------------------------------------------------------------

  const userScope = useAuthStore((state) => state.user?.scope);
  const parentAgencyName = useAuthStore(
    (state) => state.user?.parent_agency_name,
  );
  const parentOrgName = useAuthStore((state) => state.user?.parent_org_name);
  const canRefreshMart = userScope === "platform" || userScope === "tenant";

  const refreshMutation = useMutation({
    mutationFn: reportService.refreshMart,
    onSuccess: () => {
      toast.success(
        t("userReports.refreshSuccess") ||
          "Data refresh initiated. This may take a few minutes.",
      );
    },
    onError: () => {
      toast.error(
        t("userReports.refreshError") ||
          "Data refresh failed. Please try again.",
      );
    },
  });

  const handleDownloadReport = useCallback(async () => {
    const publishedPayload = reportData?.responseData?.published_payload;
    if (!publishedPayload) {
      toast.error(t("reports.downloadError") || "Report data not available");
      return;
    }

    const dateFrom = reportData?.responseData?.date_from;
    const dateTo = reportData?.responseData?.date_to;
    const reportIdValue = reportData?.responseData?.report_id;

    // Cancel any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setDownloadState("generating");
    try {
      // Step 1: Trigger PDF generation
      const genResp = await reportService.generateReportPDF({
        ...publishedPayload,
        date_from: dateFrom,
        date_to: dateTo,
        report_id: reportIdValue,
        agency_name: parentAgencyName,
        org_name: parentOrgName,
      });
      if (!genResp.success)
        throw new Error(genResp.message ?? "Failed to generate report");

      let job = genResp.responseData;

      // Step 2: Poll if still pending/running (async jobs)
      if (job.status === "PENDING" || job.status === "RUNNING") {
        setDownloadState("polling");
        job = await pollReportJob(job.job_id, controller.signal);
      } else if (job.status === "FAILED") {
        throw new Error(job.error_message ?? "Report generation failed");
      }

      // Step 3: Download the binary PDF file
      const blob = await reportService.downloadReportFile(job.job_id);
      const prefix = language === "ja" ? "顧客レポート" : "customer_report";
      const safeOrgName = (parentOrgName ?? "").replace(
        /[^a-zA-Z0-9\u3000-\u9fff\u30a0-\u30ff\u3040-\u309f]/g,
        "_",
      );
      const fileName =
        job.file?.file_name ?? `${prefix}_${safeOrgName}_${job.job_id}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloadState("idle");
      toast.success(
        t("reports.downloadSuccess") || "Report downloaded successfully",
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const msg =
        err instanceof Error ? err.message : "Failed to download report";
      setDownloadState("error");
      toast.error(msg || (t("reports.downloadError") ?? "Download failed"));
    }
  }, [reportData, t]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Data transformation (mirrors Reports/index.tsx logic)
  // -------------------------------------------------------------------------

  const report = reportData?.responseData;
  // The org-scope API wraps published data inside published_payload instead
  // of directly on responseData — fall back to root level for admin endpoints.
  const payload = report?.published_payload ?? report;
  const metrics = payload?.metrics?.summary;
  const aiInsights = payload?.ai_insights;

  // Traffic Sources
  const trafficSourcesRaw = (payload?.metrics?.traffic_sources ??
    []) as TrafficSourceRow[];
  const trafficSourcesData: TrafficSourceData = {
    sources: trafficSourcesRaw.filter(
      (r) => r.default_channel_group !== "TOTAL",
    ),
    total:
      trafficSourcesRaw.find((r) => r.default_channel_group === "TOTAL") ??
      null,
  };

  // Creatives
  const creativesRaw = (payload?.metrics?.by_creative ?? []) as CreativeRow[];
  const creativesData: CreativeData = {
    creatives: creativesRaw.filter((r) => r.creative_name !== "TOTAL"),
    total: creativesRaw.find((r) => r.creative_name === "TOTAL") ?? null,
  };

  // Campaigns — 3-level drilldown: Campaign > Ad Group > Ad
  // Reads from published_payload.metrics.by_campaign_ad (org-scope field)
  const campaignsAdRaw = (payload?.metrics?.by_campaign_ad ??
    []) as CampaignAdRow[];

  const campaignWithAdsMap = new Map<string, CampaignWithAds>();
  const adGroupByCampaignMap = new Map<
    string,
    Map<string, CampaignAdGroupByAd>
  >();

  campaignsAdRaw.forEach((ad) => {
    const campaignKey =
      ad.campaign_id || ad.campaign_name || "unknown_campaign";
    const adGroupKey =
      ad.ad_group_id || ad.ad_group_name || "unknown_ad_group";

    if (!campaignWithAdsMap.has(campaignKey)) {
      campaignWithAdsMap.set(campaignKey, {
        campaign_id: campaignKey,
        campaign_name: ad.campaign_name,
        platform: ad.platform,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: null,
        cvr: 0,
        revenue_mom: null,
        revenue_yoy: null,
        ad_groups: [],
      });
    }
    if (!adGroupByCampaignMap.has(campaignKey)) {
      adGroupByCampaignMap.set(campaignKey, new Map());
    }

    const campaign = campaignWithAdsMap.get(campaignKey)!;
    campaign.total_clicks += ad.clicks;
    campaign.total_conversions += ad.conversions;
    // Only accumulate revenue when it is non-null and > 0. If all ads have 0 or null revenue the
    // total stays null so the UI can display "N/A" instead of ¥0.
    if (ad.revenue != null && ad.revenue > 0) {
      campaign.total_revenue = (campaign.total_revenue ?? 0) + ad.revenue;
    }

    const adGroupMap = adGroupByCampaignMap.get(campaignKey)!;
    if (!adGroupMap.has(adGroupKey)) {
      adGroupMap.set(adGroupKey, {
        ad_group_id: adGroupKey,
        ad_group_name: ad.ad_group_name,
        total_clicks: 0,
        total_conversions: 0,
        total_revenue: null,
        cvr: 0,
        revenue_mom: null,
        revenue_yoy: null,
        ads: [],
      });
    }

    const adGroup = adGroupMap.get(adGroupKey)!;
    adGroup.total_clicks += ad.clicks;
    adGroup.total_conversions += ad.conversions;
    if (ad.revenue != null && ad.revenue > 0) {
      adGroup.total_revenue = (adGroup.total_revenue ?? 0) + ad.revenue;
    }
    adGroup.ads.push(ad);
  });

  // Calculate CVR + weighted MoM/YoY at ad group and campaign levels
  campaignWithAdsMap.forEach((campaign) => {
    const adGroupMap = adGroupByCampaignMap.get(campaign.campaign_id);
    if (!adGroupMap) return;

    const groups = Array.from(adGroupMap.values());

    groups.forEach((group) => {
      group.cvr =
        group.total_clicks > 0
          ? group.total_conversions / group.total_clicks
          : 0;

      let groupRevPrev = 0;
      let groupRevYoyPrev = 0;
      group.ads.forEach((ad) => {
        if (ad.revenue != null && ad.revenue_mom !== null)
          groupRevPrev += ad.revenue / (1 + ad.revenue_mom);
        if (ad.revenue != null && ad.revenue_yoy !== null)
          groupRevYoyPrev += ad.revenue / (1 + ad.revenue_yoy);
      });
      group.revenue_mom =
        group.total_revenue != null && groupRevPrev > 0
          ? (group.total_revenue - groupRevPrev) / groupRevPrev
          : null;
      group.revenue_yoy =
        group.total_revenue != null && groupRevYoyPrev > 0
          ? (group.total_revenue - groupRevYoyPrev) / groupRevYoyPrev
          : null;
    });

    campaign.ad_groups = groups;
    campaign.cvr =
      campaign.total_clicks > 0
        ? campaign.total_conversions / campaign.total_clicks
        : 0;

    let campRevPrev = 0;
    let campRevYoyPrev = 0;
    groups.forEach((group) => {
      group.ads.forEach((ad) => {
        if (ad.revenue != null && ad.revenue_mom !== null)
          campRevPrev += ad.revenue / (1 + ad.revenue_mom);
        if (ad.revenue != null && ad.revenue_yoy !== null)
          campRevYoyPrev += ad.revenue / (1 + ad.revenue_yoy);
      });
    });
    campaign.revenue_mom =
      campaign.total_revenue != null && campRevPrev > 0
        ? (campaign.total_revenue - campRevPrev) / campRevPrev
        : null;
    campaign.revenue_yoy =
      campaign.total_revenue != null && campRevYoyPrev > 0
        ? (campaign.total_revenue - campRevYoyPrev) / campRevYoyPrev
        : null;
  });

  const campaignAdTotals: CampaignAdTotals = campaignsAdRaw.reduce(
    (acc, row) => {
      acc.total_impressions = (acc.total_impressions ?? 0) + (row.impressions || 0);
      acc.total_clicks += row.clicks || 0;
      acc.total_conversions += row.conversions || 0;
      acc.total_cost = (acc.total_cost ?? 0) + (row.cost || 0);
      if (row.revenue != null && row.revenue > 0) {
        acc.total_revenue = (acc.total_revenue ?? 0) + row.revenue;
      }
      return acc;
    },
    {
      total_impressions: 0,
      total_clicks: 0,
      total_conversions: 0,
      total_cost: 0,
      total_revenue: null,
    } as CampaignAdTotals,
  );

  const campaignAdData: CampaignAdData = {
    campaigns: Array.from(campaignWithAdsMap.values()),
    totals: campaignAdTotals,
  };

  // -------------------------------------------------------------------------
  // AI Insights processing
  // Org users receive: narrative, highlights[], recommendations[]
  // They may also receive performance_overview if the backend includes it.
  // We handle both gracefully.
  // -------------------------------------------------------------------------

  // highlights[] is available for org users (not in the TS type, but sent by API)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawHighlights: any[] = (aiInsights as any)?.highlights ?? [];

  // Key Achievements: performance_overview.achievements → fallback to positive highlights
  const achievements: {
    title: string;
    desc: string;
    metric?: string;
    current_value?: string;
  }[] = aiInsights?.performance_overview?.achievements?.length
    ? aiInsights.performance_overview.achievements.map((a) => ({
        title: a.title,
        desc:
          a.summary ??
          [a.current_value, a.comparison].filter(Boolean).join(" — "),
        metric: a.metric,
        current_value: a.current_value,
      }))
    : rawHighlights
        .filter((h) => h.type === "positive")
        .map((h) => splitText(h.text as string));

  // Areas for Improvement: performance_overview.improvements → fallback to non-positive highlights
  const improvements: {
    title: string;
    desc: string;
    severity?: string;
    area?: string;
  }[] = aiInsights?.performance_overview?.improvements?.length
    ? aiInsights.performance_overview.improvements.map((imp) => ({
        title: imp.title,
        desc: imp.summary ?? [imp.area, imp.metric].filter(Boolean).join(" — "),
        severity: imp.severity,
        area: imp.area,
      }))
    : rawHighlights
        .filter((h) => h.type !== "positive")
        .map((h) => splitText(h.text as string));

  // Channel Characteristics: performance_overview → fallback to top-4 traffic sources
  const channelChars: { title: string; desc: string }[] = aiInsights
    ?.performance_overview?.channel_characteristics?.length
    ? aiInsights.performance_overview.channel_characteristics.map((ch) => ({
        title: ch.display_name || ch.channel,
        desc: ch.summary,
      }))
    : trafficSourcesData.sources
        .slice()
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 4)
        .map((src) => ({
          title: src.default_channel_group,
          desc: `${formatCompactNumber(src.sessions)} sessions · CVR ${formatPercentage(src.cvr)} · ${formatCompactNumber(src.conversions)} conversions`,
        }));

  // Recommendations (available for org users per spec)
  const recommendations = aiInsights?.recommendations ?? [];

  const hasPerformanceSection =
    achievements.length > 0 ||
    improvements.length > 0 ||
    channelChars.length > 0;

  // -------------------------------------------------------------------------
  // Loading Skeleton
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div
                className={`h-8 w-64 rounded-lg animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
              />
              <div
                className={`h-4 w-48 rounded animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
              />
            </div>
            <div
              className={`h-10 w-32 rounded-xl animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
            />
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`rounded-3xl p-6 border ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
              >
                <div
                  className={`h-4 w-24 rounded mb-4 animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                />
                <div
                  className={`h-9 w-28 rounded mb-4 animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                />
                <div className="flex gap-2">
                  <div
                    className={`h-6 w-20 rounded-lg animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  />
                  <div
                    className={`h-6 w-20 rounded-lg animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Content sections */}
          {[1, 2].map((i) => (
            <div
              key={i}
              className={`rounded-3xl p-6 border ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`h-6 w-48 rounded mb-6 animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
              />
              <div className="space-y-3">
                {[1, 2, 3].map((j) => (
                  <div
                    key={j}
                    className={`h-16 w-full rounded-xl animate-pulse ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Error State
  // -------------------------------------------------------------------------

  if (isError || (reportData && !reportData.success)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
        <div
          className={`rounded-3xl p-8 border text-center ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <p
            className={`text-lg font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {t("reports.error") || "Error loading report"}
          </p>
          <p
            className={`text-sm mb-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {(error as Error)?.message ??
              t("reports.tryAgain") ??
              "Please try again."}
          </p>
          <button
            onClick={() => navigate("/app/my-reports")}
            className="px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-all"
          >
            {t("reports.backToMyReports") || "Back to My Reports"}
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="space-y-6">
        {/* ---------------------------------------------------------------- */}
        {/* Breadcrumb                                                       */}
        {/* ---------------------------------------------------------------- */}
        <nav
          className={`flex items-center gap-2 text-sm ${
            darkMode ? "text-gray-400" : "text-gray-600"
          }`}
          aria-label="Breadcrumb"
        >
          <button
            onClick={() => navigate("/app/my-reports")}
            className="hover:text-[var(--brand-primary)] transition-colors"
          >
            {t("reports.myReports") || "My Reports"}
          </button>
          <ChevronRight className="w-4 h-4" />
          <span
            className={darkMode ? "text-gray-200" : "text-gray-800"}
            aria-current="page"
          >
            {t("reports.reportDetails") || "Report Details"}
          </span>
        </nav>

        {/* ---------------------------------------------------------------- */}
        {/* Header                                                           */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2
              className={`text-2xl sm:text-3xl font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("userReports.title")}
            </h2>
          </div>

          {/* Action buttons: Refresh Data (agency/platform only) + Download */}
          {report && (
            <div className="flex items-center gap-2 flex-wrap">
              {canRefreshMart && (
                <button
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[var(--brand-primary)] text-white rounded-xl hover:opacity-90 transition-all text-sm font-medium shadow-sm disabled:opacity-60"
                >
                  {refreshMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("userReports.refreshing") || "Refreshing…"}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      {t("userReports.refreshData") || "Refresh Data"}
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleDownloadReport}
                disabled={
                  downloadState === "generating" || downloadState === "polling"
                }
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--brand-secondary)] text-white rounded-xl hover:opacity-90 transition-all text-sm font-medium shadow-sm disabled:opacity-60"
              >
                {downloadState === "generating" ||
                downloadState === "polling" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {downloadState === "generating"
                      ? t("reports.generating") || "Generating…"
                      : t("reports.processing") || "Processing…"}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {t("reports.downloadReport") || "Download Report"}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Date range banner */}
        {report?.date_from && report?.date_to && (
          <div
            className={`flex flex-wrap items-center gap-2 px-5 py-3 rounded-2xl border ${
              darkMode
                ? "bg-gray-800/60 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <FileText
              className={`w-4 h-4 flex-shrink-0 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <span
              className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {t("reports.viewingFor") || "Viewing report for"}
            </span>
            <Clock
              className={`w-4 h-4 flex-shrink-0 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <span
              className={`text-sm font-semibold ${
                darkMode ? "text-gray-100" : "text-gray-900"
              }`}
            >
              {new Date(report.date_from).toLocaleDateString()}
              {" – "}
              {new Date(report.date_to).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Key Results Summary (4 metric cards)                             */}
        {/* ---------------------------------------------------------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-4 xl:gap-6">
          {/* Sessions */}
          <div
            className={`rounded-3xl p-4 sm:p-5 lg:p-4 xl:p-6 border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Label + Icon row — icon is flex-shrink-0 so it never compresses */}
            <div className="flex items-center justify-between mb-2">
              <p
                className={`text-xs lg:text-xs xl:text-sm font-medium truncate pr-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("userReports.sessions")}
              </p>
              <div className="w-9 h-9 flex-shrink-0 bg-[var(--brand-primary)]/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-4 h-4 xl:w-5 xl:h-5 text-[var(--brand-primary)]" />
              </div>
            </div>
            <h3
              className={`text-2xl lg:text-xl xl:text-3xl font-bold mb-3 leading-none ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {metrics
                ? formatCompactNumber(metrics.acquisition.sessions)
                : "—"}
            </h3>
            {/* MoM / YoY — 2-col grid so badges never wrap unpredictably */}
            <div className="grid grid-cols-2 gap-1.5">
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.acquisition.sessions_yoy, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.acquisition.sessions_yoy)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">YoY</span>
              </div>
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.acquisition.sessions_mom, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.acquisition.sessions_mom)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">MoM</span>
              </div>
            </div>
          </div>

          {/* Conversions */}
          <div
            className={`rounded-3xl p-4 sm:p-5 lg:p-4 xl:p-6 border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className={`text-xs lg:text-xs xl:text-sm font-medium truncate pr-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("userReports.conversions")}
              </p>
              <div className="w-9 h-9 flex-shrink-0 bg-[var(--brand-accent)]/20 rounded-xl flex items-center justify-center">
                <Activity className="w-4 h-4 xl:w-5 xl:h-5 text-[var(--brand-accent-dark)]" />
              </div>
            </div>
            <h3
              className={`text-2xl lg:text-xl xl:text-3xl font-bold mb-3 leading-none ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {metrics
                ? formatCompactNumber(metrics.conversion.conversions)
                : "—"}
            </h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.conversions_yoy, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.conversions_yoy)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">YoY</span>
              </div>
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.conversions_mom, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.conversions_mom)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">MoM</span>
              </div>
            </div>
          </div>

          {/* Conversion Rate */}
          <div
            className={`rounded-3xl p-4 sm:p-5 lg:p-4 xl:p-6 border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className={`text-xs lg:text-xs xl:text-sm font-medium truncate pr-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("userReports.conversionRate")}
              </p>
              <div className="w-9 h-9 flex-shrink-0 bg-[var(--brand-secondary)]/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-4 h-4 xl:w-5 xl:h-5 text-[var(--brand-secondary)]" />
              </div>
            </div>
            <h3
              className={`text-2xl lg:text-xl xl:text-3xl font-bold mb-3 leading-none ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {metrics ? formatPercentage(metrics.conversion.cvr) : "—"}
            </h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.cvr_yoy, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.cvr_yoy)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">YoY</span>
              </div>
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.cvr_mom, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.cvr_mom)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">MoM</span>
              </div>
            </div>
          </div>

          {/* Conversion Value */}
          <div
            className={`rounded-3xl p-4 sm:p-5 lg:p-4 xl:p-6 border ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className={`text-xs lg:text-xs xl:text-sm font-medium truncate pr-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("userReports.conversionValue")}
              </p>
              <div className="w-9 h-9 flex-shrink-0 bg-[var(--brand-primary)]/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-4 h-4 xl:w-5 xl:h-5 text-[var(--brand-primary)]" />
              </div>
            </div>
            <h3
              className={`text-2xl lg:text-xl xl:text-3xl font-bold mb-3 leading-none ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {metrics
                ? formatCompactCurrency(metrics.conversion.revenue)
                : "—"}
            </h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.revenue_yoy, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.revenue_yoy)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">YoY</span>
              </div>
              <div
                className={`flex flex-col items-center px-1.5 py-1.5 rounded-lg ${changeBadgeClass(metrics?.conversion.revenue_mom, darkMode)}`}
              >
                <span className="text-xs font-semibold leading-none">
                  {formatChange(metrics?.conversion.revenue_mom)}
                </span>
                <span className="text-[10px] opacity-70 mt-0.5">MoM</span>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Performance Overview                                             */}
        {/* ---------------------------------------------------------------- */}
        {hasPerformanceSection && (
          <section
            className={`rounded-3xl p-4 sm:p-6 border space-y-6 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div>
              <h2
                className={`text-lg sm:text-xl font-bold mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}
              >
                {t("userReports.performanceOverview")}
              </h2>
              <p
                className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("userReports.performanceSubtitle")}
              </p>
            </div>

            {/* Key Achievements */}
            {achievements.length > 0 && (
              <div className="space-y-4">
                <h3
                  className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("userReports.keyAchievements")}
                </h3>
                <div className="border-l-4 border-[var(--brand-primary)] pl-4 space-y-4">
                  {achievements.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            {item.title && (
                              <p
                                className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {item.title}
                              </p>
                            )}
                            {item.metric && item.current_value && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  darkMode
                                    ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)] border border-[var(--brand-primary)]/40"
                                    : "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/30"
                                }`}
                              >
                                {item.metric}: {item.current_value}
                              </span>
                            )}
                          </div>
                          {item.desc && (
                            <p
                              className={`text-sm leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {item.desc}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas for Improvement */}
            {improvements.length > 0 && (
              <div className="space-y-4">
                <h3
                  className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("userReports.areasForImprovement")}
                </h3>
                <div className="border-l-4 border-[var(--brand-secondary)] pl-4 space-y-4">
                  {improvements.map((item, idx) => {
                    const sev = item.severity?.toLowerCase();
                    const severityPillClass =
                      sev === "high"
                        ? darkMode
                          ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                          : "bg-rose-50 text-rose-600 border border-rose-200"
                        : sev === "medium"
                          ? darkMode
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "bg-amber-50 text-amber-600 border border-amber-200"
                          : sev === "low"
                            ? darkMode
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                              : "bg-emerald-50 text-emerald-600 border border-emerald-200"
                            : null;

                    return (
                      <div key={idx}>
                        <div className="flex gap-3">
                          <ArrowDownRight className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                              {item.title && (
                                <p
                                  className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {item.title}
                                </p>
                              )}
                              {severityPillClass && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${severityPillClass}`}
                                >
                                  {item.severity}
                                </span>
                              )}
                            </div>
                            {item.area && (
                              <p
                                className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Area: {item.area}
                              </p>
                            )}
                            {item.desc && (
                              <p
                                className={`text-sm leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                {item.desc}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Channel Characteristics */}
            {channelChars.length > 0 && (
              <div className="space-y-4">
                <h3
                  className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {t("userReports.channelCharacteristics")}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {channelChars.map((ch, idx) => (
                    <div
                      key={idx}
                      className={`bg-gradient-to-br rounded-2xl p-4 border ${
                        idx % 2 === 0
                          ? darkMode
                            ? "from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/30"
                            : "from-[var(--brand-primary)]/5 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/20"
                          : darkMode
                            ? "from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/30"
                            : "from-[var(--brand-secondary)]/5 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/20"
                      }`}
                    >
                      <h4
                        className={`text-sm font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {ch.title}
                      </h4>
                      <p
                        className={`text-xs sm:text-sm leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {ch.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Recommended Next Actions                                         */}
        {/* ---------------------------------------------------------------- */}
        {recommendations.length > 0 && (
          <section
            className={`rounded-3xl p-4 sm:p-6 border space-y-6 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <h2
              className={`text-lg sm:text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("userReports.recommendedNextActions")}
            </h2>

            <div className="space-y-4">
              {recommendations.map((rec, idx) => {
                const isHigh = rec.impact === "high";
                const isMedium = rec.impact === "medium";

                const impactLabel = isHigh
                  ? t("reports.highImpact") || "High Impact"
                  : isMedium
                    ? t("reports.mediumImpact") || "Medium Impact"
                    : t("reports.lowImpact") || "Low Impact";

                const ImpactIcon = isHigh
                  ? TrendingUp
                  : isMedium
                    ? Activity
                    : Minus;

                const impactPillClass = isHigh
                  ? darkMode
                    ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                    : "bg-rose-50 text-rose-600 border border-rose-200"
                  : isMedium
                    ? darkMode
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                      : "bg-amber-50 text-amber-600 border border-amber-200"
                    : darkMode
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                      : "bg-emerald-50 text-emerald-600 border border-emerald-200";

                const impactDotColor = isHigh
                  ? "var(--brand-secondary)"
                  : isMedium
                    ? "#f59e0b"
                    : "var(--brand-primary)";

                return (
                  <div
                    key={rec.id ?? idx}
                    className={`rounded-2xl border p-4 space-y-3 ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    {/* Header: number + title + impact pill */}
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                          darkMode
                            ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                            : "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p
                            className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {rec.title}
                          </p>
                          {/* Graphical impact badge */}
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${impactPillClass}`}
                          >
                            <ImpactIcon className="w-3 h-3" />
                            {impactLabel}
                          </span>
                        </div>
                        {rec.description && (
                          <p
                            className={`text-sm leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {rec.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Expected Impact box — shows expected_outcomes list */}
                    <div
                      className={`rounded-lg p-3 border ${
                        darkMode
                          ? "bg-gray-700/50 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <p
                        className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <ImpactIcon
                          className="w-3.5 h-3.5"
                          style={{ color: impactDotColor }}
                        />
                        {t("userReports.expectedImpact")}
                      </p>
                      {rec.expected_outcomes &&
                      rec.expected_outcomes.length > 0 ? (
                        <ul className="space-y-1">
                          {rec.expected_outcomes.map((outcome, oi) => (
                            <li
                              key={oi}
                              className={`text-xs flex items-start gap-1.5 ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              <span
                                className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: impactDotColor }}
                              />
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {impactLabel}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Traffic by Channel                                               */}
        {/* ---------------------------------------------------------------- */}
        <TrafficSourceTable data={trafficSourcesData} showTotals={false} />

        {/* ---------------------------------------------------------------- */}
        {/* Campaign Performance (Campaign > Ad Group > Ad 3-level drill)   */}
        {/* ---------------------------------------------------------------- */}
        <MetricsByCampaignAdGroupAdTable
          data={campaignAdData}
          showExport={false}
        />

        {/* ---------------------------------------------------------------- */}
        {/* Creative Performance                                             */}
        {/* ---------------------------------------------------------------- */}
        {creativesData.creatives.length > 0 ? (
          <MetricsByCreativeTable data={creativesData} showTotals={false} />
        ) : (
          <div
            className={`rounded-3xl p-6 border text-center ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("reports.creativePerformanceData")}
            </h3>
            <p
              className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {t("reports.noDataAvailable") || "No data available"}
            </p>
          </div>
        )}
      </div>

      <Toaster />
    </div>
  );
}
